import { useEffect, useState } from "react";
import Modal from "@/components/base/Modal";
import Button from "@/components/base/Button";
import { Input, Textarea } from "@/components/base/Field";
import { useToast } from "@/components/base/Toast";
import { createRole, updateRole, type PermissionModule } from "@/services/userApi";
import PermissionMatrix from "./PermissionMatrix";
import type { CycleScope, Role } from "@/types";
import { plural } from "@/utils/format";

interface RoleFormModalProps {
  open: boolean;
  onClose: () => void;
  role: Role | null;
  modules: PermissionModule[];
  onSaved: () => void;
}

const CYCLE_OPTIONS: { value: CycleScope; label: string; icon: string; hint: string }[] = [
  { value: "PRIMAIRE", label: "Primaire", icon: "ri-leaf-line", hint: "Ne verra que les données du primaire" },
  { value: "SECONDAIRE", label: "Secondaire", icon: "ri-book-2-line", hint: "Ne verra que les données du secondaire" },
  { value: "GLOBAL", label: "Global", icon: "ri-global-line", hint: "Verra les deux cycles" },
];

export default function RoleFormModal({ open, onClose, role, modules, onSaved }: RoleFormModalProps) {
  const { notify } = useToast();
  const isEdit = role !== null;
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [cycleScope, setCycleScope] = useState<CycleScope>("GLOBAL");
  const [permissions, setPermissions] = useState<string[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setErrors({});
    if (role) {
      setName(role.name);
      setDescription(role.description);
      setCycleScope(role.cycleScope);
      setPermissions([...role.permissions]);
    } else {
      setName("");
      setDescription("");
      setCycleScope("GLOBAL");
      setPermissions([]);
    }
  }, [open, role]);

  /** Permissions sélectionnables pour le périmètre choisi. */
  const allowedFor = (scope: CycleScope) =>
    new Set(
      modules.flatMap((m) => m.permissions.filter((p) => scope === "GLOBAL" || !p.globalOnly).map((p) => p.id)),
    );

  const changeScope = (scope: CycleScope) => {
    setCycleScope(scope);
    const allowed = allowedFor(scope);
    setPermissions((prev) => prev.filter((id) => allowed.has(id)));
  };

  const handleSave = async () => {
    const next: Record<string, string> = {};
    if (!name.trim()) next.name = "Le nom du rôle est obligatoire.";
    setErrors(next);
    if (Object.keys(next).length > 0) {
      notify("Le nom du rôle est obligatoire.", "error");
      return;
    }
    setSaving(true);
    try {
      const dto = { name: name.trim(), description: description.trim(), cycleScope, permissions };
      if (isEdit && role) {
        await updateRole(role.id, dto);
        notify(`Rôle « ${dto.name} » mis à jour.`);
      } else {
        await createRole(dto);
        notify(`Rôle « ${dto.name} » créé.`);
      }
      onSaved();
      onClose();
    } catch (err) {
      notify(err instanceof Error ? err.message : "Une erreur est survenue lors de l'enregistrement.", "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={isEdit ? "Modifier le rôle" : "Créer un rôle"}
      subtitle="Le cycle et les permissions définissent ce que l'utilisateur peut voir"
      icon="ri-shield-keyhole-line"
      size="xl"
      footer={
        <>
          <span className="mr-auto text-xs text-foreground-500">
            {plural(permissions.length, "permission")} sélectionnée(s)
          </span>
          <Button variant="outline" onClick={onClose}>
            Annuler
          </Button>
          <Button icon="ri-save-3-line" onClick={handleSave} disabled={saving}>
            {saving ? "Enregistrement..." : "Enregistrer"}
          </Button>
        </>
      }
    >
      <div className="space-y-5">
        <div className="grid grid-cols-1 gap-4">
          <Input
            label="Nom du rôle"
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
            hint={errors.name}
          />
          <Textarea
            label="Description"
            rows={2}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="À quoi sert ce rôle, qui l'utilise..."
          />
        </div>

        <div>
          <p className="mb-2 text-xs font-medium text-foreground-700">
            Cycle du rôle <span className="text-accent-600">*</span>
          </p>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
            {CYCLE_OPTIONS.map((opt) => {
              const on = cycleScope === opt.value;
              return (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => changeScope(opt.value)}
                  className={`flex items-start gap-3 rounded-lg border p-3 text-left transition-colors cursor-pointer ${
                    on
                      ? "border-primary-400 bg-primary-50"
                      : "border-background-200 bg-background-50 hover:border-background-300"
                  }`}
                >
                  <span
                    className={`w-8 h-8 rounded-md flex items-center justify-center shrink-0 ${
                      on ? "bg-primary-600 text-white" : "bg-secondary-100 text-secondary-700"
                    }`}
                  >
                    <i className={`${opt.icon} text-base`}></i>
                  </span>
                  <span>
                    <span className="block text-sm font-medium text-foreground-900">{opt.label}</span>
                    <span className="block text-xs text-foreground-500">{opt.hint}</span>
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        <div>
          <div className="mb-2 flex items-center justify-between">
            <p className="text-xs font-medium text-foreground-700">Permissions</p>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => setPermissions([...allowedFor(cycleScope)])}
                className="text-xs font-medium text-primary-700 hover:underline cursor-pointer"
              >
                Tout sélectionner
              </button>
              <button
                type="button"
                onClick={() => setPermissions([])}
                className="text-xs font-medium text-foreground-500 hover:underline cursor-pointer"
              >
                Tout retirer
              </button>
            </div>
          </div>
          <PermissionMatrix modules={modules} selected={permissions} onChange={setPermissions} cycleScope={cycleScope} />
        </div>
      </div>
    </Modal>
  );
}